import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, LOGO } from "@/lib/api";

const SiteCtx = createContext({ site: null, reload: () => {} });
const CartCtx = createContext(null);
const CART_KEY = "vrv_basket";

export function SiteProvider({ children }) {
  const [site, setSite] = useState(null);
  const reload = useCallback(() => api.get("/site").then((r) => setSite(r.data)), []);
  useEffect(() => {
    reload();
  }, [reload]);
  useEffect(() => {
    if (!site) return;
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = site.favicon_url ? `${process.env.REACT_APP_BACKEND_URL}${site.favicon_url}` : LOGO;
  }, [site]);
  return <SiteCtx.Provider value={{ site, reload }}>{children}</SiteCtx.Provider>;
}
export const useSite = () => useContext(SiteCtx);
export const useLogo = () => {
  const { site } = useSite();
  return site?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${site.logo_url}` : LOGO;
};

const snap = (p) => ({
  artist: p.artist, title: p.title, edition: p.edition, format: p.format, price: p.price,
  image: p.image, stock: p.stock, sku: p.sku,
});

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CART_KEY)) || [];
    } catch {
      return [];
    }
  });
  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  const value = useMemo(() => {
    const add = (p, qty = 1) =>
      setItems((cur) => {
        const ex = cur.find((i) => i.product_id === p.id);
        if (ex) return cur.map((i) => (i.product_id === p.id ? { ...i, qty: Math.min(i.qty + qty, p.stock || 20) } : i));
        return [...cur, { product_id: p.id, qty, snapshot: snap(p) }];
      });
    const setQty = (id, qty) => setItems((cur) => cur.map((i) => (i.product_id === id ? { ...i, qty: Math.max(1, qty) } : i)));
    const remove = (id) => setItems((cur) => cur.filter((i) => i.product_id !== id));
    const refresh = (products) =>
      setItems((cur) =>
        cur.filter((i) => products.some((p) => p.id === i.product_id))
          .map((i) => ({ ...i, snapshot: snap(products.find((p) => p.id === i.product_id)) }))
      );
    const clear = () => setItems([]);
    const count = items.reduce((s, i) => s + i.qty, 0);
    const subtotal = items.reduce((s, i) => s + i.qty * i.snapshot.price, 0);
    return { items, add, setQty, remove, refresh, clear, count, subtotal };
  }, [items]);
  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}
export const useCart = () => useContext(CartCtx);

const setMeta = (attr, key, content) => {
  let m = document.querySelector(`meta[${attr}="${key}"]`);
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute(attr, key);
    document.head.appendChild(m);
  }
  m.setAttribute("content", content || "");
};

export function usePageMeta(title, description, keywords, image) {
  useEffect(() => {
    if (title) {
      document.title = title;
      setMeta("property", "og:title", title);
    }
    if (description != null) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
    }
    setMeta("name", "keywords", keywords || "");
    setMeta("property", "og:image", image ? (image.startsWith("http") ? image : `${window.location.origin}${image}`) : `${window.location.origin}/logo.png`);
  }, [title, description, keywords, image]);
}
